/**
 * Finding detail drawer (modal dialog, deep-linkable with ?finding=<id>): evidence, business impact, fix + steps
 * checklist, competitor benchmark, Brain rules in plain words, history timeline, status controls, and the T5 Resolve /
 * T10 Advisor slots. Keyboard: focus moves into the dialog, Tab stays inside, Escape closes, focus returns to the opener.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import type { AdvancedFinding, AdvancedReport, FindingStatus } from '@qm/contracts/audit-remediation';
import { Badge, Button, Text } from '@qm/ui';
import { brainRefLabel, isOwnerRole, statusActions, canWriteFindings, type StatusAction } from './model';
import { AdvisorSlot, ResolveSlot } from './slots';
import type { ReportStore, ReportUiState } from './store';
import { L, useReportText } from './text';
import { severityTone, statusTone } from './tones';
import { ResolvabilityBadge } from './FindingsSection';

export interface FindingDrawerProps {
  report: AdvancedReport;
  finding: AdvancedFinding;
  state: ReportUiState;
  store: ReportStore;
  roles: readonly string[] | undefined;
  onClose: () => void;
}

export function FindingDrawer({ report, finding: f, state, store, roles, onClose }: FindingDrawerProps) {
  const { t, num, loc, dir, dateTime: fmtDate } = useReportText();
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    const opener = typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;
    ref.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    return () => { opener?.focus?.(); };
  }, [f.id]);

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
    if (e.key !== 'Tab' || !ref.current) return;
    const items = [...ref.current.querySelectorAll<HTMLElement>('a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])')];
    if (!items.length) return;
    const first = items[0]!, last = items[items.length - 1]!;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const ourScore = report.signals.find((s) => s.id === f.signal)?.score ?? null;
  const history = state.history[f.id];
  const slotProps = {
    jobId: report.jobId, finding: f, locale: loc, canWrite: canWriteFindings(roles), canApprove: isOwnerRole(roles),
    onFindingChanged: () => { void store.reloadReport(); void store.loadHistory(f.id); },
  };

  return (
    <div className="qm-ar-drawer-wrap">
      <div className="qm-ar-backdrop" aria-hidden="true" onClick={onClose} />
      <div ref={ref} className="qm-ar-drawer" role="dialog" aria-modal="true" aria-labelledby={titleId} lang={loc} dir={dir} onKeyDown={onKey}>
        <header className="qm-ar-drawer__head">
          <div className="qm-ar-inline">
            <Badge tone={severityTone(f.severity)}>{t(`audit-remediation.severity.${f.severity}`)}</Badge>
            <Badge tone={statusTone(f.status)}>{t(`audit-remediation.status.${f.status}`)}</Badge>
            <ResolvabilityBadge f={f} />
            <span className="qm-ar-muted">{t('audit-remediation.report.priorityShort', { n: num(f.priority) })} · {t(`audit-remediation.roadmap.${f.roadmapSlot}`)}</span>
          </div>
          <h2 id={titleId} className="qm-ar-drawer__title"><L v={f.title} /></h2>
          <p className="qm-ar-muted">
            {t(`audit-remediation.report.signal.${f.signal}`)} · {t(`audit-remediation.source.${f.source}`)} · {t(`audit-remediation.report.owner.${f.owner}`)} · {t(`audit-remediation.report.effort.${f.effort}`)}
            {f.regulatory ? <> · <span className="qm-ar-flag">{t('audit-remediation.report.regulatory')}</span></> : null}
          </p>
          <Button size="sm" variant="ghost" className="qm-ar-drawer__close" data-autofocus onClick={onClose} aria-label={t('audit-remediation.report.drawer.close')}>×</Button>
        </header>

        <div className="qm-ar-drawer__body">
          <section aria-labelledby={`${titleId}-fix`} className="qm-ar-slotwrap">
            <h3 id={`${titleId}-fix`} className="qm-ar-h3">{t('audit-remediation.report.drawer.resolve')}</h3>
            <ResolveSlot {...slotProps} />
          </section>

          <StatusControls key={`${f.id}:${f.status}`} f={f} state={state} store={store} roles={roles} />

          <section aria-labelledby={`${titleId}-ev`}>
            <h3 id={`${titleId}-ev`} className="qm-ar-h3">{t('audit-remediation.report.drawer.evidence')}</h3>
            <L v={f.evidence} as="p" />
            <ul className="qm-ar-evidence">
              {f.evidenceItems.map((e, i) => (
                <li key={`${e.checkId}-${i}`}>
                  <p><code dir="ltr">{e.checkId}</code> · {t('audit-remediation.report.drawer.capturedAt')} <time dateTime={e.capturedAt}>{fmtDate(e.capturedAt)}</time></p>
                  {e.count !== null ? <p>{t('audit-remediation.report.drawer.count', { n: num(e.count) })}</p> : null}
                  {e.quote ? <blockquote lang="en" dir="auto">{e.quote}</blockquote> : null}
                  {e.pages.length ? (
                    <ul className="qm-ar-pages" aria-label={t('audit-remediation.report.drawer.pages')}>
                      {e.pages.map((p) => <li key={p}>{/^https:\/\//.test(p) ? <a href={p} target="_blank" rel="noopener noreferrer" dir="ltr">{p}</a> : <bdi dir="ltr">{p}</bdi>}</li>)}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>

          <section aria-labelledby={`${titleId}-impact`}>
            <h3 id={`${titleId}-impact`} className="qm-ar-h3">{t('audit-remediation.report.drawer.impact')}</h3>
            <L v={f.impact} as="p" />
          </section>

          <section aria-labelledby={`${titleId}-how`}>
            <h3 id={`${titleId}-how`} className="qm-ar-h3">{t('audit-remediation.report.drawer.fix')}</h3>
            <L v={f.fix} as="p" />
            <Steps f={f} />
          </section>

          {report.competitors.length ? (
            <section aria-labelledby={`${titleId}-bench`}>
              <h3 id={`${titleId}-bench`} className="qm-ar-h3">{t('audit-remediation.report.drawer.benchmark', { signal: t(`audit-remediation.report.signal.${f.signal}`) })}</h3>
              <ul className="qm-ar-bench">
                <li><b>{t('audit-remediation.report.competitors.you')}</b><span>{ourScore === null ? t('audit-remediation.report.notMeasuredShort') : num(ourScore)}</span></li>
                {report.competitors.map((c) => {
                  const s = c.signals[f.signal];
                  const ahead = s !== null && ourScore !== null && s > ourScore;
                  return (
                    <li key={c.name} data-ahead={ahead ? 'true' : undefined}>
                      <bdi>{c.domain ?? c.name}</bdi>
                      <span>{s === null ? t('audit-remediation.report.notMeasuredShort') : num(s)}{ahead ? ` · ${t('audit-remediation.report.drawer.ahead')}` : ''}</span>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          {f.brainRefs.length ? (
            <section aria-labelledby={`${titleId}-rules`}>
              <h3 id={`${titleId}-rules`} className="qm-ar-h3">{t('audit-remediation.report.drawer.rules')}</h3>
              <ul className="qm-ar-list">
                {f.brainRefs.map((r) => {
                  const b = brainRefLabel(r);
                  return <li key={r}><span>{t(`audit-remediation.report.brain.${b.kind}`)}</span><span lang="en" dir="auto">{b.area ? `${b.area}: ${b.rule}` : b.rule}</span></li>;
                })}
              </ul>
              <Text tone="muted">{t('audit-remediation.report.drawer.rulesNote')}</Text>
            </section>
          ) : null}

          <section aria-labelledby={`${titleId}-hist`}>
            <h3 id={`${titleId}-hist`} className="qm-ar-h3">{t('audit-remediation.report.drawer.history')}</h3>
            {!history || history.status === 'loading' ? <p className="qm-loading" role="status">{t('audit-remediation.report.loading')}</p>
              : history.status === 'error' ? <p className="qm-error" role="alert">{t('audit-remediation.report.errors.history')}</p>
                : !history.data.length ? <Text tone="muted">{t('audit-remediation.report.drawer.noHistory')}</Text> : (
                  <ol className="qm-timeline">
                    {[...history.data].reverse().map((h) => (
                      <li key={h.id} className="qm-timeline__step" data-state="done">
                        <b>{t('audit-remediation.report.drawer.historyRow', { from: t(`audit-remediation.status.${h.from}`), to: t(`audit-remediation.status.${h.to}`) })}</b>
                        <span><time dateTime={h.at}>{fmtDate(h.at)}</time> · {t(`audit-remediation.report.actor.${h.actor}`)}</span>
                        {h.reason ? <q lang="en" dir="auto">{h.reason}</q> : null}
                      </li>
                    ))}
                  </ol>
                )}
          </section>

          <section aria-labelledby={`${titleId}-adv`} className="qm-ar-slotwrap">
            <h3 id={`${titleId}-adv`} className="qm-ar-h3">{t('audit-remediation.report.drawer.advisor')}</h3>
            <AdvisorSlot {...slotProps} subject={report.subject} />
          </section>
        </div>
      </div>
    </div>
  );
}

function Steps({ f }: { f: AdvancedFinding }) {
  const { t } = useReportText();
  const [done, setDone] = useState<Set<number>>(new Set());
  return (
    <>
      <ol className="qm-ar-steps" aria-label={t('audit-remediation.report.drawer.steps')}>
        {f.steps.map((s, i) => {
          const id = `ar-step-${f.id}-${i}`;
          return (
            <li key={i}>
              <label htmlFor={id} className="qm-ar-check">
                <input id={id} type="checkbox" checked={done.has(i)} onChange={() => setDone((d) => { const n = new Set(d); if (n.has(i)) n.delete(i); else n.add(i); return n; })} />
                <L v={s} />
              </label>
            </li>
          );
        })}
      </ol>
      <Text tone="muted">{t('audit-remediation.report.drawer.stepsNote')}</Text>
    </>
  );
}

const ACTION_VARIANT: Record<StatusAction['kind'], 'primary' | 'secondary' | 'ghost'> = { start: 'primary', reopen: 'secondary', pause: 'secondary', dismiss: 'ghost', accept: 'ghost' };

export function StatusControls({ f, state, store, roles }: { f: AdvancedFinding; state: ReportUiState; store: ReportStore; roles: readonly string[] | undefined }) {
  const { t } = useReportText();
  const actions = statusActions(f.status, roles);
  const [pending, setPending] = useState<StatusAction | null>(null);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const busy = state.busy === `status:${f.id}`;
  const fieldId = `ar-reason-${f.id}`;

  const go = async (a: StatusAction, why = '') => {
    setErr(null);
    const r = await store.changeStatus(f.id, a.to as FindingStatus, why);
    if (r.ok) { setPending(null); setReason(''); } else setErr(r.errorKey);
  };

  return (
    <section className="qm-ar-status" aria-labelledby={`ar-status-${f.id}`}>
      <h3 id={`ar-status-${f.id}`} className="qm-ar-h3">{t('audit-remediation.report.status.title')}</h3>
      {f.statusReason ? <p>{t('audit-remediation.report.status.reason')} <q lang="en" dir="auto">{f.statusReason}</q></p> : null}
      {!actions.length ? (
        <Text tone="muted">{t(canWriteFindings(roles) ? 'audit-remediation.report.status.engineHeld' : 'audit-remediation.report.status.readOnly')}</Text>
      ) : (
        <div className="qm-ar-inline">
          {actions.map((a) => (
            <Button key={a.to} size="sm" variant={ACTION_VARIANT[a.kind]} disabled={busy}
              aria-expanded={a.needsReason ? pending?.to === a.to : undefined}
              onClick={() => (a.needsReason ? (setPending(a), setErr(null)) : void go(a))}>
              {t(`audit-remediation.report.status.action.${a.kind}`)}
            </Button>
          ))}
        </div>
      )}
      {pending ? (
        <form className="qm-ar-reason" onSubmit={(e) => { e.preventDefault(); void go(pending, reason); }} noValidate>
          <div className="qm-field" data-invalid={err ? 'true' : undefined}>
            <label className="qm-field__label" htmlFor={fieldId}>{t(`audit-remediation.report.status.reasonLabel.${pending.kind}`)}</label>
            <textarea id={fieldId} className="qm-input" rows={3} maxLength={1000} value={reason} required aria-required="true"
              aria-invalid={err ? true : undefined} aria-describedby={`${fieldId}-hint${err ? ` ${fieldId}-err` : ''}`} onChange={(e) => setReason(e.currentTarget.value)} />
            <p className="qm-field__hint" id={`${fieldId}-hint`}>{t('audit-remediation.report.status.reasonHint')}</p>
            {err ? <p className="qm-field__error" id={`${fieldId}-err`} role="alert">{t(err)}</p> : null}
          </div>
          <div className="qm-ar-inline">
            <Button type="submit" size="sm" variant="primary" disabled={busy}>{t(`audit-remediation.report.status.confirm.${pending.kind}`)}</Button>
            <Button size="sm" variant="ghost" onClick={() => { setPending(null); setErr(null); }}>{t('audit-remediation.report.cancel')}</Button>
          </div>
        </form>
      ) : err ? <p className="qm-field__error" role="alert">{t(err)}</p> : null}
    </section>
  );
}
